import api from '@/services/api';
import type {
  DealerConfigOption,
  DealerConfigOptionBulkLinkResult,
  DealerConfigOptionListParams,
  DealerConfigOptionPaginationMeta,
  DealerConfigOptionsPage,
} from '../types/dealerConfigOption.types';

/**
 * CRITICAL — the catalog base path is `/config-options` (public path
 * `/api/config-options`; the shared Axios client's base URL is `/api`).
 *
 * It must NOT be `/dealers/config-options`. A browser call to
 * `/api/dealers/config-options` is caught by the existing gateway
 * `location /api/dealers/` block (`infrastructure/nginx/docker/default.conf`)
 * and forwarded to dealers-nginx as `dealers/config-options`, which matches
 * `Route::resource('dealers', ...)`'s `show` route (`dealers/{dealer}`) with
 * `{dealer} = "config-options"` — hitting `DealerController@show`, NOT the
 * catalog. The only collision-free public path for the catalog is
 * `/config-options`, reachable via the folded-in gateway
 * `location /api/config-options/` block added alongside this service.
 */
const configOptionsPath = '/config-options';

function extractMeta(data: Record<string, unknown>): DealerConfigOptionPaginationMeta {
  return {
    current_page: data.current_page as number,
    last_page: data.last_page as number,
    per_page: data.per_page as number,
    total: data.total as number,
    from: data.from as number,
    to: data.to as number,
  };
}

export const fetchDealerConfigOptions = async (
  params: DealerConfigOptionListParams = {},
): Promise<DealerConfigOptionsPage> => {
  // Trailing slash is required here (mirrors `dealers.ts`'s `${dealersPath}/`):
  // the bare list request resolves to `/api/config-options` with nothing
  // appended, which does NOT match the nginx prefix location
  // `location /api/config-options/` (it requires the URI to start with that
  // exact string, trailing slash included). `/config-options/{id}` calls
  // below need no such fix — the id segment already satisfies the prefix.
  const response = await api.get(`${configOptionsPath}/`, { params });
  const raw = response.data as Record<string, unknown>;
  return { data: (raw.data ?? []) as DealerConfigOption[], meta: extractMeta(raw) };
};

export const fetchDealerConfigOption = async (id: string): Promise<DealerConfigOption> => {
  const response = await api.get(`${configOptionsPath}/${id}`);
  return (response.data.data ?? response.data) as DealerConfigOption;
};

export const bulkLinkDealerConfigOption = async (
  id: string,
  opts: { force?: boolean } = {},
): Promise<DealerConfigOptionBulkLinkResult> => {
  const body = opts.force ? { force: true } : {};
  const response = await api.post(`${configOptionsPath}/${id}/bulk-link`, body);
  return (response.data.data ?? response.data) as DealerConfigOptionBulkLinkResult;
};
